import * as React from 'react';
import { useMemo } from 'react';

import useFieldTypes from 'data-lake/hooks/useFieldTypes';
import { Spinner } from 'components/common';
import { Alert } from 'components/bootstrap';
import { DocumentationLink } from 'components/support';
import DocsHelper from 'util/DocsHelper';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import { DEFAULT_FIELDS } from 'data-lake/preview/storage-types/external/Constants';

import PreviewLayoutContext from '../../../contexts/PreviewLayoutContext';

const FieldSelectInfo = ({ isLoadingFields, tableId = undefined }: { isLoadingFields: boolean; tableId?: string }) => {
  if (isLoadingFields) {
    return <Spinner text="Loading field types" />;
  }

  if (!tableId) {
    return (
      <Alert bsStyle="warning">
        Please <b>select a table</b> in the search bar above or in the table list on the left side, before choosing
        fields for this widget.
      </Alert>
    );
  }

  return null;
};

const WidgetEditInfo = () => (
  <Alert>
    The fields shown above come from the schema of the selected Security Lake table and vary depending on the data
    source. See <DocumentationLink page={DocsHelper.PAGES.DATA_LAKE} text="dcocumentation" />.
  </Alert>
);

const PreviewLayoutProvider = ({ children }: React.PropsWithChildren) => {
  const { isLoading: isLoadingFields } = useFieldTypes();
  const { dataLakeFieldsFetchQuery } = useSelectedDataLake();

  const contextValue = useMemo(
    () => ({
      widgetEdit: {
        fieldSelectInfo: (
          <FieldSelectInfo isLoadingFields={isLoadingFields} tableId={dataLakeFieldsFetchQuery?.tableId} />
        ),
        formInfo: <WidgetEditInfo />,
        showFieldSelect: !isLoadingFields && !!dataLakeFieldsFetchQuery?.tableId,
        fieldsCreationAllowed: false,
      },
      defaultFields: DEFAULT_FIELDS,
      timestampField: 'time_dt',
    }),
    [dataLakeFieldsFetchQuery?.tableId, isLoadingFields],
  );

  return <PreviewLayoutContext.Provider value={contextValue}>{children}</PreviewLayoutContext.Provider>;
};

export default PreviewLayoutProvider;
